export type ToolActivityClusterUserIntent = 'user-opened' | 'user-collapsed';

const MAX_REMEMBERED_MEMBER_IDS = 256;
const rememberedIntentByMemberId = new Map<
  string,
  ToolActivityClusterUserIntent
>();

export function getToolActivityClusterMemberIds({
  clusterId,
  entryKeys,
}: {
  clusterId: string;
  entryKeys: readonly string[];
}): string[] {
  const memberIds = [clusterId];
  for (const entryKey of entryKeys) {
    if (entryKey && entryKey !== clusterId) {
      memberIds.push(entryKey);
    }
  }
  return memberIds;
}

export function nextToolActivityClusterIntent({
  userIntent,
  userSetOpen,
}: {
  userIntent: ToolActivityClusterUserIntent | null;
  activityCount?: number;
  nextActivityCount?: number;
  userSetOpen?: boolean;
}): ToolActivityClusterUserIntent | null {
  if (userSetOpen === true) return 'user-opened';
  if (userSetOpen === false) return 'user-collapsed';
  return userIntent;
}

export function resolveToolActivityClusterOpenState({
  userIntent,
}: {
  userIntent: ToolActivityClusterUserIntent | null;
  activityCount?: number;
}): boolean {
  return userIntent === 'user-opened';
}

export function toggleToolActivityClusterIntent(
  userIntent: ToolActivityClusterUserIntent | null,
  activityCount?: number
): ToolActivityClusterUserIntent {
  return (
    nextToolActivityClusterIntent({
      userIntent,
      activityCount,
      nextActivityCount: activityCount,
      userSetOpen: !resolveToolActivityClusterOpenState({
        userIntent,
        activityCount,
      }),
    }) ?? 'user-opened'
  );
}

export function rememberToolActivityClusterIntent(
  memberIds: readonly string[],
  intent: ToolActivityClusterUserIntent
) {
  for (const memberId of memberIds) {
    if (!memberId) continue;
    rememberedIntentByMemberId.delete(memberId);
    rememberedIntentByMemberId.set(memberId, intent);
  }
  while (rememberedIntentByMemberId.size > MAX_REMEMBERED_MEMBER_IDS) {
    const oldestMemberId = rememberedIntentByMemberId.keys().next().value;
    if (!oldestMemberId) break;
    rememberedIntentByMemberId.delete(oldestMemberId);
  }
}

export function recallToolActivityClusterIntent(
  memberIds: readonly string[]
): ToolActivityClusterUserIntent | null {
  for (const memberId of memberIds) {
    const intent = rememberedIntentByMemberId.get(memberId);
    if (intent) return intent;
  }
  return null;
}

export function clearToolActivityClusterDisclosureStore() {
  rememberedIntentByMemberId.clear();
}
